/**
 * Ticket 124.6c — tenant-hierarchy client used for parent detection.
 *
 * `GET /api/v1/admin/tenant-hierarchy` returns one node per tenant in the
 * caller's visible hierarchy. Nodes carry `managementMode` (SELF|MANAGED)
 * and `hasDescendants` alongside id/name/slug/parentTenantId/depth. The
 * subtree roll-up view uses `hasDescendants` on the caller's own node to
 * decide whether the parent-only view should render.
 */

import { apiClient } from '@/lib/apiClient';

export type ManagementMode = 'SELF' | 'MANAGED';

export interface TenantHierarchyNode {
  id: string;
  name: string;
  slug: string;
  parentTenantId: string | null;
  depth: number;
  managementMode: ManagementMode;
  hasDescendants: boolean;
}

/** Fetch the caller's visible tenant hierarchy. */
export async function fetchTenantHierarchy(): Promise<TenantHierarchyNode[]> {
  const { data } = await apiClient.get<TenantHierarchyNode[]>('/admin/tenant-hierarchy');
  return data;
}

/**
 * True when the given tenant's own node exists in the hierarchy and has
 * descendants — i.e. the caller is a parent and should see the subtree
 * roll-up. Returns false for a leaf tenant, an unknown tenant, or a
 * missing id.
 */
export function isParentTenant(
  nodes: TenantHierarchyNode[],
  tenantId: string | undefined,
): boolean {
  if (!tenantId) return false;
  return nodes.some((n) => n.id === tenantId && n.hasDescendants);
}
